import {
  FilterOptionKeys,
  Goal,
  GoalFilterDocument,
  GoalSortingMode,
  ID,
} from '@griply/types';
import { getSubGoals, sortGoals } from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useMemo } from 'react';
import { sortGoalsHierarchically } from 'shared/utils/sort-goals-hierarchically';

export type Options = {
  customOrder?: ID[];
};

export const useGoalsByFilterDocument = (
  filterDocument: GoalFilterDocument | undefined,
  { customOrder }: Options,
) => {
  const sortedGoals = useSortedGoals();

  return useMemo(() => {
    const filters = filterDocument?.filters ?? {};
    const filteredGoals = Object.keys(filters).reduce<Goal[]>(
      (acc, filterKey) => {
        if (
          filterKey === FilterOptionKeys.LifeArea &&
          filters[filterKey]?.include
        ) {
          const lifeAreaIds = filters[filterKey]?.include;
          if (!lifeAreaIds?.length) {
            return [];
          }

          // Root goals (directly in life area)
          const validIds = new Set<ID>();

          const rootGoals = acc.filter(
            (goal) => goal.lifeAreaId && lifeAreaIds.includes(goal.lifeAreaId),
          );

          rootGoals.forEach((rootGoal) => {
            validIds.add(rootGoal.id);

            const subGoals = getSubGoals(rootGoal.id, {
              goals: acc,
              recursive: true,
            });
            subGoals.forEach((subGoal) => validIds.add(subGoal.id));
          });

          return acc.filter((goal) => validIds.has(goal.id));
        }

        if (
          filterKey === FilterOptionKeys.Goal &&
          filters[filterKey]?.include
        ) {
          const goalIds = filters[filterKey]?.include;
          return goalIds?.length
            ? acc.filter(
                (goal) =>
                  goalIds.includes(goal.id) ||
                  (goal.parentIds?.length &&
                    goal.parentIds.some((parentId) =>
                      goalIds.includes(parentId),
                    )),
              )
            : [];
        }

        return acc;
      },
      sortedGoals,
    );

    // sort filtered goals
    if (customOrder?.length) {
      return sortGoalsHierarchically(filteredGoals, customOrder);
    }

    return sortGoals(filteredGoals, {
      allGoals: sortedGoals,
      sortMode: filterDocument?.sortingMode ?? GoalSortingMode.StartDate,
      customOrder,
    });
  }, [
    customOrder,
    filterDocument?.filters,
    filterDocument?.sortingMode,
    sortedGoals,
  ]);
};
